import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { pharmacies, PHARMACY_SERVICES, type Pharmacy } from '@/mocks/pharmacies';
import PharmacyFilters, { type FilterState } from './components/PharmacyFilters';
import PharmacyCard from './components/PharmacyCard';
import PharmacyDetail from './components/PharmacyDetail';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const MAP_URL =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d144699.5!2d37.3858!3d55.7558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000000!5m2!1sru!2sru';

const defaultFilters = (): FilterState => ({
  search: '',
  district: 'Все районы',
  services: [],
  onlyOpen: false,
  only24h: false,
  onlyDelivery: false,
  onlyDriveThru: false,
  sortBy: 'distance',
});

const pharmaciesJsonLd = [
  // ── WebPage + BreadcrumbList ──────────────────────────────────────────────
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Аптеки NeonPharma на карте Москвы',
    description: 'Найдите ближайшую аптеку NeonPharma в Москве. Расписание работы, услуги, адреса и телефоны. Более 8 аптек по всей Москве.',
    url: `${SITE_URL}/pharmacies`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Аптеки', item: `${SITE_URL}/pharmacies` },
      ],
    },
  },
  // ── MedicalOrganization — сеть аптек ─────────────────────────────────────
  {
    '@context': 'https://schema.org',
    '@type': ['MedicalOrganization', 'Organization'],
    name: 'NeonPharma',
    url: SITE_URL,
    logo: 'https://public.readdy.ai/ai/img_res/1641229f-de9d-42a9-901f-6e578c489111.png',
    description: 'Сеть аптек NeonPharma в Москве. Более 8 аптек, доставка за 2 часа, работаем 24/7.',
    telephone: '8 800 123-45-67',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Москва',
      addressCountry: 'RU',
    },
    areaServed: {
      '@type': 'City',
      name: 'Москва',
    },
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 200 },
    foundingDate: '2020',
    sameAs: [
      'https://t.me/',
      'https://vk.com/',
    ],
  },
  // ── ItemList — все аптеки с полными LocalBusiness данными ────────────────
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Аптеки NeonPharma в Москве',
    description: 'Полный список аптек сети NeonPharma в Москве с адресами, телефонами и расписанием работы.',
    url: `${SITE_URL}/pharmacies`,
    numberOfItems: pharmacies.length,
    itemListElement: pharmacies.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': ['Pharmacy', 'LocalBusiness', 'MedicalOrganization'],
        '@id': `${SITE_URL}/pharmacies/${p.id}`,
        name: p.name,
        description: p.description,
        url: `${SITE_URL}/pharmacies/${p.id}`,
        telephone: p.phone,
        priceRange: '₽₽',
        address: {
          '@type': 'PostalAddress',
          streetAddress: p.address,
          addressLocality: 'Москва',
          addressRegion: 'Москва',
          addressCountry: 'RU',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: p.coords.lat,
          longitude: p.coords.lng,
        },
        hasMap: `https://maps.google.com/?q=${p.coords.lat},${p.coords.lng}`,
        openingHoursSpecification: p.is24h
          ? [{
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
              opens: '00:00',
              closes: '23:59',
            }]
          : p.schedule.map(s => ({
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ({ Пн: 'Monday', Вт: 'Tuesday', Ср: 'Wednesday', Чт: 'Thursday', Пт: 'Friday', Сб: 'Saturday', Вс: 'Sunday' } as Record<string, string>)[s.day] ?? s.day,
              opens: s.open,
              closes: s.close === '24:00' ? '23:59' : s.close,
            })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: p.rating,
          reviewCount: p.reviews,
          bestRating: 5,
          worstRating: 1,
        },
        image: p.image,
        parentOrganization: {
          '@type': 'Organization',
          name: 'NeonPharma',
          url: SITE_URL,
        },
      },
    })),
  },
];

const PharmaciesPage = () => {
  const [filters, setFilters] = useState<FilterState>(defaultFilters());
  const [selected, setSelected] = useState<Pharmacy | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'split' | 'list' | 'map'>('split');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    let list = [...pharmacies];

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.address.toLowerCase().includes(q) ||
        p.metro.toLowerCase().includes(q) ||
        p.district.toLowerCase().includes(q)
      );
    }
    if (filters.district !== 'Все районы') list = list.filter(p => p.district === filters.district);
    if (filters.services.length > 0) list = list.filter(p => filters.services.every(s => p.services.includes(s)));
    if (filters.onlyOpen) list = list.filter(p => p.isOpen || p.is24h);
    if (filters.only24h) list = list.filter(p => p.is24h);
    if (filters.onlyDelivery) list = list.filter(p => p.hasDelivery);
    if (filters.onlyDriveThru) list = list.filter(p => p.hasDriveThru);

    switch (filters.sortBy) {
      case 'rating': return [...list].sort((a, b) => b.rating - a.rating);
      case 'reviews': return [...list].sort((a, b) => b.reviews - a.reviews);
      default: return [...list].sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
    }
  }, [filters]);

  const openCount = pharmacies.filter(p => p.isOpen || p.is24h).length;
  const h24Count = pharmacies.filter(p => p.is24h).length;

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title="Аптеки NeonPharma на карте Москвы — расписание и услуги"
        description="Найдите ближайшую аптеку NeonPharma в Москве. Расписание работы, адреса, телефоны, услуги. Более 8 аптек, 2 работают 24/7. Доставка из аптеки."
        keywords="аптеки на карте, аптека рядом, NeonPharma Москва, расписание аптеки, услуги аптеки"
        canonical="/pharmacies"
        jsonLd={pharmaciesJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-10 px-4 md:px-8 lg:px-12 overflow-hidden">
        {/* BG glows */}
        <div className="absolute top-0 left-1/4 w-96 h-64 opacity-8 rounded-full pointer-events-none" style={{ background: '#ff2d78', filter: 'blur(100px)' }} />
        <div className="absolute top-10 right-1/4 w-64 h-64 opacity-6 rounded-full pointer-events-none" style={{ background: '#39ff14', filter: 'blur(80px)' }} />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-6">
            <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color: '#ff2d78' }}>Аптеки</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(to bottom, #ff2d78, transparent)' }} />
                <span className="font-mono text-xs tracking-widest uppercase" style={{ color: 'rgba(255,45,120,0.7)' }}>
                  Сеть аптек
                </span>
              </div>
              <h1 className="font-orbitron font-black text-3xl md:text-5xl text-white leading-tight">
                АПТЕКИ<br />
                <span style={{ color: '#ff2d78', textShadow: '0 0 30px rgba(255,45,120,0.4)' }}>НА КАРТЕ</span>
              </h1>
              <p className="font-mono text-sm text-white/40 mt-3 max-w-lg leading-relaxed">
                Найдите ближайшую аптеку, проверьте расписание и доступные услуги. Более {pharmacies.length} аптек по всей Москве.
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-4 flex-wrap">
              {[
                { val: pharmacies.length, label: 'Аптек', icon: 'ri-store-2-line', color: '#ff2d78' },
                { val: openCount, label: 'Открыто', icon: 'ri-door-open-line', color: '#39ff14' },
                { val: h24Count, label: 'Работают 24/7', icon: 'ri-moon-line', color: '#00f5ff' },
                { val: PHARMACY_SERVICES.length, label: 'Услуг', icon: 'ri-service-line', color: '#f0ff00' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-3 px-4 py-3 rounded-2xl" style={{ background: `${s.color}06`, border: `1px solid ${s.color}18` }}>
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
                    <i className={s.icon} style={{ color: s.color, fontSize: '16px' }} />
                  </div>
                  <div>
                    <div className="font-orbitron font-black text-xl" style={{ color: s.color }}>{s.val}</div>
                    <div className="font-mono text-[10px] text-white/35">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Search bar */}
          <div className="flex gap-3 flex-col sm:flex-row">
            <div
              className="flex-1 flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <i className="ri-search-line text-lg shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }} />
              <input
                ref={searchRef}
                type="text"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Адрес, метро, район..."
                className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder-white/20"
              />
              {filters.search && (
                <button onClick={() => setFilters(f => ({ ...f, search: '' }))} className="cursor-pointer text-white/30 hover:text-white/60 transition-colors shrink-0">
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            {/* View mode switcher */}
            <div className="flex items-center gap-1 p-1 rounded-2xl shrink-0" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {([
                { id: 'split', icon: 'ri-layout-column-line', label: 'Список + карта' },
                { id: 'list', icon: 'ri-list-check', label: 'Только список' },
                { id: 'map', icon: 'ri-map-2-line', label: 'Только карта' },
              ] as { id: typeof viewMode; icon: string; label: string }[]).map(v => (
                <button
                  key={v.id}
                  onClick={() => setViewMode(v.id)}
                  title={v.label}
                  className="w-10 h-10 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200"
                  style={viewMode === v.id
                    ? { background: 'rgba(255,45,120,0.2)', color: '#ff2d78' }
                    : { color: 'rgba(255,255,255,0.35)' }
                  }
                >
                  <i className={v.icon} />
                </button>
              ))}
            </div>

            {/* Mobile filters btn */}
            <button
              onClick={() => setMobileFiltersOpen(o => !o)}
              className="whitespace-nowrap lg:hidden flex items-center gap-2 px-4 py-3 rounded-2xl font-mono text-sm cursor-pointer transition-all shrink-0"
              style={mobileFiltersOpen
                ? { background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }
                : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }
              }
            >
              <i className="ri-equalizer-line" />
              Фильтры
            </button>
          </div>

          {/* Mobile filters */}
          {mobileFiltersOpen && (
            <div className="lg:hidden mt-4">
              <PharmacyFilters filters={filters} onChange={setFilters} totalCount={filtered.length} />
            </div>
          )}
        </div>
      </section>

      {/* Main content */}
      <section className="pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-6">

            {/* Sidebar — desktop */}
            <div className="hidden lg:block">
              <PharmacyFilters filters={filters} onChange={setFilters} totalCount={filtered.length} />
            </div>

            {/* Content area */}
            <div className="flex-1 min-w-0">

              {/* Results header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-orbitron font-bold text-sm text-white">
                    Найдено <span style={{ color: '#ff2d78' }}>{filtered.length}</span> аптек
                  </span>
                  {filtered.length !== pharmacies.length && (
                    <button
                      onClick={() => setFilters(defaultFilters())}
                      className="flex items-center gap-1 font-mono text-xs cursor-pointer transition-colors"
                      style={{ color: 'rgba(255,255,255,0.35)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }}
                    >
                      <i className="ri-refresh-line" />
                      Сбросить
                    </button>
                  )}
                </div>

                {/* Quick service pills */}
                <div className="hidden md:flex items-center gap-1.5 flex-wrap">
                  {PHARMACY_SERVICES.slice(0, 4).map(svc => {
                    const active = filters.services.includes(svc.id);
                    return (
                      <button
                        key={svc.id}
                        onClick={() => {
                          const next = active
                            ? filters.services.filter(s => s !== svc.id)
                            : [...filters.services, svc.id];
                          setFilters(f => ({ ...f, services: next }));
                        }}
                        className="whitespace-nowrap flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px] cursor-pointer transition-all duration-200"
                        style={active
                          ? { background: `${svc.color}18`, border: `1px solid ${svc.color}45`, color: svc.color }
                          : { border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }
                        }
                      >
                        <i className={`${svc.icon} text-[10px]`} />
                        {svc.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Split / List / Map layout */}
              {viewMode === 'map' ? (
                /* Full map */
                <div className="rounded-2xl overflow-hidden" style={{ height: '600px', border: '1px solid rgba(0,245,255,0.2)' }}>
                  <iframe
                    src={MAP_URL}
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.7) brightness(0.85)' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Аптеки NeonPharma на карте"
                  />
                </div>
              ) : (
                <div className={`flex gap-5 ${viewMode === 'split' ? 'flex-col xl:flex-row' : 'flex-col'}`}>

                  {/* List */}
                  <div className={viewMode === 'split' ? 'xl:w-[420px] shrink-0' : 'w-full'}>
                    {filtered.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-4" style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)' }}>
                          <i className="ri-store-2-line text-2xl" style={{ color: '#ff2d78' }} />
                        </div>
                        <h3 className="font-orbitron font-bold text-lg text-white mb-2">Аптеки не найдены</h3>
                        <p className="font-mono text-sm text-white/40 mb-4">Попробуйте изменить фильтры или поисковый запрос</p>
                        <button
                          onClick={() => setFilters(defaultFilters())}
                          className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-sm cursor-pointer transition-all"
                          style={{ border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}
                        >
                          <i className="ri-refresh-line" />
                          Сбросить фильтры
                        </button>
                      </div>
                    ) : (
                      <div className={viewMode === 'list' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-4'}>
                        {filtered.map(p => (
                          <PharmacyCard
                            key={p.id}
                            pharmacy={p}
                            isSelected={selected?.id === p.id}
                            onSelect={() => setSelected(prev => prev?.id === p.id ? null : p)}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Map + detail panel */}
                  {viewMode === 'split' && (
                    <div className="flex-1 min-w-0 flex flex-col gap-4">
                      {/* Map */}
                      <div
                        className="relative rounded-2xl overflow-hidden"
                        style={{ height: selected ? '320px' : '520px', border: '1px solid rgba(0,245,255,0.2)', transition: 'height 0.4s cubic-bezier(0.4,0,0.2,1)' }}
                      >
                        {/* Corner accents */}
                        {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((pos, i) => (
                          <div key={i} className={`absolute ${pos} w-6 h-6 z-10 pointer-events-none`}>
                            <div className="absolute inset-0" style={{
                              borderTop: (i < 2) ? '1px solid #00f5ff' : 'none',
                              borderBottom: (i >= 2) ? '1px solid #00f5ff' : 'none',
                              borderLeft: (i % 2 === 0) ? '1px solid #00f5ff' : 'none',
                              borderRight: (i % 2 === 1) ? '1px solid #00f5ff' : 'none',
                            }} />
                          </div>
                        ))}

                        {/* Scanlines */}
                        <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,245,255,0.012) 2px, rgba(0,245,255,0.012) 4px)' }} />

                        <iframe
                          src={MAP_URL}
                          width="100%"
                          height="100%"
                          style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.7) brightness(0.85)' }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Аптеки NeonPharma"
                        />

                        {/* Map label */}
                        <div className="absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-1.5 rounded-xl font-mono text-xs pointer-events-none"
                          style={{ background: 'rgba(5,5,8,0.85)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff', backdropFilter: 'blur(8px)' }}
                        >
                          <i className="ri-map-2-line" />
                          {filtered.length} аптек на карте
                        </div>
                      </div>

                      {/* Detail panel */}
                      {selected && (
                        <div style={{ animation: 'fadeInUp 0.35s cubic-bezier(0.22,1,0.36,1)' }}>
                          <PharmacyDetail pharmacy={selected} onClose={() => setSelected(null)} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Detail panel in list mode */}
              {viewMode === 'list' && selected && (
                <div className="mt-6" style={{ animation: 'fadeInUp 0.35s cubic-bezier(0.22,1,0.36,1)' }}>
                  <PharmacyDetail pharmacy={selected} onClose={() => setSelected(null)} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-6 rounded-full" style={{ background: 'linear-gradient(to bottom, #ff2d78, transparent)' }} />
            <h2 className="font-orbitron font-black text-2xl text-white">УСЛУГИ В АПТЕКАХ</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {PHARMACY_SERVICES.map(svc => {
              const count = pharmacies.filter(p => p.services.includes(svc.id)).length;
              return (
                <button
                  key={svc.id}
                  onClick={() => {
                    const active = filters.services.includes(svc.id);
                    setFilters(f => ({
                      ...f,
                      services: active ? f.services.filter(s => s !== svc.id) : [...f.services, svc.id],
                    }));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl cursor-pointer transition-all duration-300 group"
                  style={{
                    background: filters.services.includes(svc.id) ? `${svc.color}10` : 'rgba(255,255,255,0.02)',
                    border: filters.services.includes(svc.id) ? `1px solid ${svc.color}40` : '1px solid rgba(255,255,255,0.07)',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${svc.color}35`; }}
                  onMouseLeave={e => { if (!filters.services.includes(svc.id)) (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110"
                    style={{ background: `${svc.color}15`, border: `1px solid ${svc.color}30` }}
                  >
                    <i className={svc.icon} style={{ color: svc.color, fontSize: '22px' }} />
                  </div>
                  <div className="text-center">
                    <div className="font-mono text-xs text-white/70 leading-snug">{svc.label}</div>
                    <div className="font-mono text-[10px] mt-0.5" style={{ color: `${svc.color}80` }}>{count} аптек</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PharmaciesPage;
